/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, ChangeEvent } from 'react';
import dayjs from 'dayjs';
import {
  Sizes,
  Positions,
  Icons,
  TextInput,
  Card,
} from '@sede-enterprise/shell-ds-react-framework';
import { useSelector } from 'react-redux';
import { ActionMenu, NGContainer } from '../NominationGroups/style';
import Table from 'src/UI/Table';
import Button from 'src/UI/Button';
import { searchByText } from 'src/utils/common/utils';
import { NUMBER_20 } from 'src/utils/table';
import { get } from 'src/services/GenesisApi';
import { DOWNLOAD_ORGANIZATIONS, GET_ORGANIZATIONS, MESSAGES } from 'src/constants/EUMarkets';
import DownloadButton from 'src/UI/Download';
import { COMMON_MESSAGES } from 'src/constants/Common';
import { sendNotification } from 'src/UI/Notification';
import { IFullState } from 'src/types/All.type';
import { rolePermissions } from 'src/constants/Permission';
import { IOrganization } from 'src/types/EUMarkets.type';
import { getEnv } from 'src/utils/env.vite';
import Parameters from '../Parameters';
import AddEditOrganization from './AddEdit';

const { ArrowCcw, Search, Add: AddIcon, EllipsisVertical } = Icons;

// Add/Modify are only available in the Dev environment until the save APIs are ready elsewhere.
const isDevEnvironment = () => getEnv('VITE_APP_ENVIRONMENT') === 'Dev';

const Organizations: React.FC = () => {
  const [organizations, setOrganizations] = useState<IOrganization[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [openAddEdit, setOpenAddEdit] = useState<boolean>(false);
  const [organization, setOrganization] = useState<IOrganization | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const { permissions } = useSelector((state: IFullState) => state.genesisUserRolePermission);

  const getData = () => {
    // API call to fetch data
    setLoading(true);
    get(GET_ORGANIZATIONS)
      .then((response) => {
        setOrganizations(response.data || []);
      })
      .catch(() => {
        sendNotification({
          type: 'error',
          message: MESSAGES.SERVER_ERROR,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getData();
  }, []);

  const handleModalOpenClose = () => {
    if (open) {
      setOrganization(null);
    }
    setOpen(!open);
  };

  const handleAddEditOpenClose = () => {
    if (openAddEdit) {
      setOrganization(null);
    }
    setOpenAddEdit(!openAddEdit);
  };

  const handleEdit = (item: IOrganization) => {
    setOrganization(item);
    setOpenAddEdit(true);
  };

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const renderData = (text: string, data: IOrganization[]) => {
    return searchByText(data, text, [
      'organizationName',
      'shortName',
      'internalOrganizationName',
      'externalOrganizationName',
    ]);
  };

  const handleShowParams = (item: IOrganization) => {
    setOrganization(item);
    setOpen(true);
  };

  const renderActions = (item: IOrganization) => (
    <ActionMenu
      action='click'
      popup={
        <div className='menu-container'>
          {isDevEnvironment() && permissions.includes(rolePermissions.updateMasterData) && (
            <Button
              data-testid={`btn-modify-${item.id}`}
              size='small'
              onClick={() => handleEdit(item)}
              variant='transparent'
            >
              Modify
            </Button>
          )}
        </div>
      }
    >
      <Button
        size='small'
        data-testid={`btn-actions-${item.id}`}
        variant='transparent'
        onClick={() => {}}
        icon={<EllipsisVertical />}
        iconOnly
      />
    </ActionMenu>
  );

  const showActions = isDevEnvironment() && permissions.includes(rolePermissions.updateMasterData);

  const columns = [
    {
      accessKey: 'organizationName',
      fieldName: 'organizationName',
      title: 'Organization Name',
      sorting: true,
    },
    {
      accessKey: 'shortName',
      fieldName: 'shortName',
      title: 'Short Name',
      sorting: true,
    },
    {
      accessKey: 'marketName',
      fieldName: 'marketName',
      title: 'Market Name',
      sorting: true,
    },
    {
      accessKey: 'validFrom',
      fieldName: 'validFrom',
      title: 'Valid From',
      sorting: true,
      render: (item: IOrganization) =>
        item.validFrom ? dayjs(item.validFrom).format(COMMON_MESSAGES.DATE_DD_MMM_YYYY) : '-',
    },
    {
      accessKey: 'validTo',
      fieldName: 'validTo',
      title: 'Valid To',
      sorting: true,
      render: (item: IOrganization) =>
        item.validTo ? dayjs(item.validTo).format(COMMON_MESSAGES.DATE_DD_MMM_YYYY) : 'No Expiry',
    },
    {
      accessKey: 'roles',
      fieldName: 'roles',
      title: 'Roles',
      render: (item) => item.roles.map((role) => role.roleName).join(', '),
    },
    {
      accessKey: 'parameters',
      fieldName: 'parameters',
      title: 'Parameters',
      render: (item) => (
        <Button
          data-testid={`btn-params-${item.id}`}
          size='small'
          onClick={() => handleShowParams(item)}
          key={`tsk-add-btn-${item.id}`}
          variant='transparent'
          className='logs-info'
        >
          View
        </Button>
      ),
    },
    ...(showActions
      ? [
          {
            accessKey: 'actions',
            fieldName: 'actions',
            title: 'Actions',
            render: (item: IOrganization) => renderActions(item),
          },
        ]
      : []),
  ];

  return (
    <NGContainer>
      <div className='action-container'>
        <div className='search-container'>
          <TextInput
            placeholder='Search'
            onChange={handleSearch}
            suffix={{
              node: <Search height={24} width={24} />,
            }}
            size={Sizes.Small}
          />
        </div>
        {isDevEnvironment() && permissions.includes(rolePermissions.createMasterData) && (
          <Button
            onClick={handleAddEditOpenClose}
            data-testid='add-new-organization'
            icon={<AddIcon />}
            size={Sizes.Small}
            variant='outlined'
            iconPosition={Positions.Right}
          >
            Add
          </Button>
        )}
        <DownloadButton
          downloadURL={DOWNLOAD_ORGANIZATIONS}
          method='GET'
          fileName='Organizations'
          fileType='xlsx'
          buttonProp={{
            size: Sizes.Small,
            variant: 'outlined',
            className: 'master-data-download master-data-download-secondary',
            iconOnly: true,
            'aria-label': 'Download Organizations as Excel',
            title: 'Download Organizations as Excel',
          }}
        />
        <Button
          size='small'
          iconOnly
          icon={<ArrowCcw />}
          onClick={getData}
          disabled={loading}
          data-testid='btn-refresh'
        />
      </div>
      <Card className='table-card'>
        <Table
          data={renderData(searchText, organizations)}
          columns={columns}
          totalRecords={renderData(searchText, organizations).length}
          recordsPerPage={NUMBER_20}
          isLoading={loading}
        />
      </Card>
      {open && (
        <Parameters
          open={open}
          onClose={handleModalOpenClose}
          params={{
            name: organization?.organizationName ?? '',
            parameters: organization?.parameters ?? [],
          }}
          paramType='Organization'
        />
      )}
      <AddEditOrganization
        open={openAddEdit}
        onClose={handleAddEditOpenClose}
        organization={organization}
        refreshData={getData}
      />
    </NGContainer>
  );
};

export default Organizations;
